import { useState } from 'react';
import { Box, CircularProgress, Link, Typography } from '@mui/material';
import ImageIcon from '@mui/icons-material/Image';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import InsertDriveFileIcon from '@mui/icons-material/InsertDriveFile';
import { fileApi } from '../api/file_api';
import type { AssociatedFileInfo } from '../api/file_association_api';

interface MessageAttachmentChipsProps {
  attachments: AssociatedFileInfo[];
}

function getFileIcon(contentType: string) {
  if (contentType.startsWith('image/')) return <ImageIcon fontSize="small" sx={{ color: 'text.secondary' }} />;
  if (contentType === 'application/pdf') return <PictureAsPdfIcon fontSize="small" sx={{ color: 'text.secondary' }} />;
  return <InsertDriveFileIcon fontSize="small" sx={{ color: 'text.secondary' }} />;
}

function formatFileSize(bytes: number | null): string {
  if (bytes === null || bytes === undefined) return '';
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

export function MessageAttachmentChips({ attachments }: MessageAttachmentChipsProps) {
  const [downloading, setDownloading] = useState<string | null>(null);

  if (!attachments || attachments.length === 0) return null;

  const handleDownload = async (attachment: AssociatedFileInfo) => {
    setDownloading(attachment.association_uuid);
    try {
      const result = await fileApi.getDownloadUrl(attachment.association_uuid, 'attachment');
      window.open(result.url, '_blank');
    } catch (err) {
      console.error('Failed to download:', err);
    } finally {
      setDownloading(null);
    }
  };

  return (
    <Box sx={{ mt: 1 }}>
      {attachments.map(attachment => (
        <Box
          key={attachment.association_uuid}
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 1,
            py: 0.5,
          }}
        >
          {downloading === attachment.association_uuid ? (
            <CircularProgress size={18} />
          ) : (
            getFileIcon(attachment.content_type)
          )}
          <Link
            component="button"
            variant="body2"
            onClick={() => handleDownload(attachment)}
            sx={{
              textAlign: 'left',
              cursor: 'pointer',
            }}
          >
            {attachment.filename}
          </Link>
          {attachment.size && (
            <Typography variant="caption" color="text.secondary">
              ({formatFileSize(attachment.size)})
            </Typography>
          )}
        </Box>
      ))}
    </Box>
  );
}
